import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, Wifi, WifiOff, BatteryFull, Volume2, Download, LogOut, User, Mic, MicOff, CheckSquare, Hash, RefreshCw, CloudOff, UserCog, Sparkles, Users, FolderKanban, LayoutGrid, StickyNote } from "lucide-react";
import { useAuth } from "./AuthContext";
import { useWM } from "./store";
import { APPS } from "./apps";
import { parseVoiceCommand, speak, stopSpeaking, probeMic, voiceErrorText, micBlockerText } from "./voice";
import { approvalsApi, teamChatApi } from "./api";
import { chatBus } from "./chatSocket";
import { mailUnread } from "./mailUnread";
import { offlineQueue } from "./offlineQueue";
import HexaBuddy from "./HexaBuddy";
import BetaChip from "./BetaChip";
import Profile from "./Profile";

export default function TopBar({ onOpenSpotlight, taskbarOpen, onToggleTaskbar, hasOpenApps, stickyNotesVisible, onToggleStickyNotes }) {
  const { user, logout, appAccess } = useAuth();
  const { open: openApp } = useWM();
  const [now, setNow] = useState(new Date());
  const [loginTime] = useState(() => {
    try {
      const stored = sessionStorage.getItem("hexa_login_ts");
      if (stored) return new Date(stored);
    } catch (_) {}
    const t = new Date();
    try { sessionStorage.setItem("hexa_login_ts", t.toISOString()); } catch (_) {}
    return t;
  });
  const [sessionElapsed, setSessionElapsed] = useState("");
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [buddyOpen, setBuddyOpen] = useState(false);
  // Local mirror purely for this button's active styling — the real state lives
  // in Taskbar, which we drive by event.
  const [taskbarShown, setTaskbarShown] = useState(false);
  // Tiny "Buddy is thinking of a tip for you" bubble that appears briefly
  // after every login so users notice the AI helper. Once dismissed (or
  // buddy opened), it stays hidden for the rest of the session via
  // sessionStorage.
  const [buddyTipVisible, setBuddyTipVisible] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem("buddyTipSeen") === "1") return;
    } catch (_) { /* ignore */ }
    const showTimer = setTimeout(() => setBuddyTipVisible(true), 1200);
    const hideTimer = setTimeout(() => {
      setBuddyTipVisible(false);
      try { sessionStorage.setItem("buddyTipSeen", "1"); } catch (_) { /* ignore */ }
    }, 12000);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, []);
  const dismissBuddyTip = () => {
    setBuddyTipVisible(false);
    try { sessionStorage.setItem("buddyTipSeen", "1"); } catch (_) { /* ignore */ }
  };
  const [voiceOn, setVoiceOn] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceFeedback, setVoiceFeedback] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [netState, setNetState] = useState({ online: typeof navigator !== "undefined" ? navigator.onLine : true, pending: 0, syncing: false });
  const menuRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => {
    const unsub = offlineQueue.subscribe(setNetState);
    setNetState({ online: navigator.onLine, pending: offlineQueue.count(), syncing: false });
    return () => unsub();
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setNow(new Date());
      setSessionElapsed(() => {
        const diffMs = Date.now() - loginTime.getTime();
        const h = Math.floor(diffMs / 3600000);
        const m = Math.floor((diffMs % 3600000) / 60000);
        const s = Math.floor((diffMs % 60000) / 1000);
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [loginTime]);

  useEffect(() => {
    if (!user) return;
    const refresh = async () => {
      try {
        const r = await approvalsApi.count();
        setPendingCount(r.pending || 0);
      } catch { /* ignore */ }
    };
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [user]);

  // Team Chat: connect socket + track unread count globally
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("aura_token");
    if (token) chatBus.connect(token);
    // Start the mail-unread bus (WS-driven inbox counts for HEXA + External).
    mailUnread.start();
    const recompute = async () => {
      try {
        const rooms = await teamChatApi.listRooms();
        const n = rooms.reduce((acc, r) => acc + (r.unread || 0), 0);
        setChatUnread(n);
      } catch { /* ignore */ }
    };
    recompute();
    const t = setInterval(recompute, 10000);
    const unsub = chatBus.subscribe((evt) => {
      if (["message", "delete", "edit", "room_updated"].includes(evt.type)) recompute();
    });
    return () => { clearInterval(t); unsub(); mailUnread.stop(); };
  }, [user]);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, []);

  const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const date = now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

  const install = async () => {
    if (installEvt) {
      installEvt.prompt();
      await installEvt.userChoice;
      setInstallEvt(null);
      return;
    }
    // Fallback: no native prompt event (Safari, Firefox, or already-prompted Chrome)
    setInstallHelpOpen(true);
  };

  const browserHint = (() => {
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
    const isFirefox = /Firefox|FxiOS/.test(ua);
    const isEdge = /Edg\//.test(ua);
    const isChromeLike = /Chrome|CriOS|Chromium/.test(ua) && !isEdge;
    if (isIOS) return "iOS Safari: tap the Share icon → Add to Home Screen.";
    if (isSafari) return "Safari (macOS): File menu → Add to Dock…";
    if (isFirefox) return "Firefox: install support is limited — try Chrome or Edge to install as an app.";
    if (isEdge) return "Edge: open the ⋯ menu → Apps → Install this site as an app.";
    if (isChromeLike) return "Chrome: click the install icon in the URL bar, or open the ⋮ menu → Install HEXA OS.";
    return "Look in your browser's menu for an Install or Add to Home Screen option.";
  })();

  const initial = (user?.name || user?.username || "?").slice(0, 1).toUpperCase();

  const runVoiceCommand = (text) => {
    setVoiceTranscript(text);
    const result = parseVoiceCommand(text);
    if (!result) {
      setVoiceFeedback(`Didn't understand: "${text}"`);
      speak("Sorry, I didn't understand that.");
      return;
    }
    if (result.action === "open" && result.appId) {
      const app = APPS.find(a => a.id === result.appId);
      if (app) { openApp(app); setVoiceFeedback(`Opened ${app.title}`); speak(`Opening ${app.title}`); }
    } else if (result.action === "scan") {
      const app = APPS.find(a => a.id === "security");
      if (app) { openApp(app); setVoiceFeedback("Opening Security & scanning"); speak("Opening security"); }
    } else if (result.action === "time") {
      const t = new Date().toLocaleTimeString();
      setVoiceFeedback(`Time: ${t}`); speak(`The time is ${t}`);
    } else if (result.action === "date") {
      const d = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
      setVoiceFeedback(d); speak(`Today is ${d}`);
    } else if (result.action === "logout") {
      speak("Signing out"); logout();
    } else if (result.action === "stop") {
      stopSpeaking(); setVoiceFeedback("Stopped.");
    }
    setTimeout(() => setVoiceFeedback(""), 4000);
  };

  const toggleVoice = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceFeedback("Voice unsupported — use Chrome or Edge on desktop.");
      alert("Voice recognition is only supported in Chrome / Edge (desktop). Please switch browsers to use voice commands.");
      return;
    }
    // Microphone must be reachable over HTTPS
    if (typeof window !== "undefined" && window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setVoiceFeedback("Voice requires HTTPS.");
      alert("Voice commands require an HTTPS connection.");
      return;
    }
    if (voiceOn) { try { recogRef.current?.stop(); } catch { /* noop */ } return; }

    setVoiceFeedback("Checking microphone…");
    const mic = await probeMic();
    const blocker = micBlockerText(mic.verdict);
    if (blocker) {
      setVoiceFeedback(blocker);
      setTimeout(() => setVoiceFeedback(""), 6000);
      return;
    }
    // Flat signal: the stream is open but carrying nothing. Warn, then still try —
    // a false reading must never be the reason voice refuses to run.
    const deadStream = mic.verdict === "silent";

    const r = new SR();
    r.lang = "en-IN"; r.continuous = false; r.interimResults = true; r.maxAlternatives = 1;
    // Chrome's voice-activity detection gives up after only 2-3 seconds of
    // silence, so clicking the mic and then pausing to think produces a
    // "no-speech" error on a perfectly good microphone. Retry once before
    // surfacing anything, which effectively doubles the window to start talking.
    let sawAnyResult = false;
    let retriedNoSpeech = false;

    r.onstart = () => {
      setVoiceOn(true);
      setVoiceFeedback(
        deadStream
          ? "Listening, but your mic is sending no sound — check the input device for this site."
          : "Listening — speak now."
      );
    };
    r.onaudiostart = () => setVoiceFeedback(deadStream ? "Listening, but no sound is arriving…" : "Listening — speak now.");
    r.onspeechend = () => setVoiceFeedback("Processing…");
    r.onend = () => setVoiceOn(false);
    r.onerror = (ev) => {
      const code = ev?.error || "unknown";

      // A working mic that simply hasn't heard anything yet: give it one more go
      // instead of reporting failure. Only when nothing has been heard at all.
      if (code === "no-speech" && !sawAnyResult && !retriedNoSpeech && !deadStream) {
        retriedNoSpeech = true;
        setVoiceFeedback("Still listening — speak now…");
        try { r.start(); return; } catch (_) { /* fall through to the message */ }
      }

      setVoiceOn(false);
      const msg = voiceErrorText(code, { deadStream });
      setVoiceFeedback(msg);
      // Persist the feedback longer for errors so the user actually reads it
      setTimeout(() => setVoiceFeedback(""), 6000);
      // eslint-disable-next-line no-console
      console.warn("[voice] recognition error:", code, ev);
    };
    r.onresult = (ev) => {
      // With interimResults=true, results can be interim; use the LAST final one
      let finalTxt = "";
      let interimTxt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const chunk = ev.results[i];
        if (chunk.isFinal) finalTxt += chunk[0].transcript;
        else               interimTxt += chunk[0].transcript;
      }
      if (interimTxt || finalTxt) sawAnyResult = true;
      if (interimTxt) setVoiceTranscript(interimTxt);
      if (finalTxt) {
        setVoiceTranscript(finalTxt);
        runVoiceCommand(finalTxt);
      }
    };
    try {
      recogRef.current = r;
      r.start();
    } catch (e) {
      setVoiceOn(false);
      setVoiceFeedback(`Could not start mic: ${e.message || e}`);
      // eslint-disable-next-line no-console
      console.error("[voice] start failed:", e);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "v") {
        e.preventDefault();
        toggleVoice();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [voiceOn]);

  return (
    <div
      data-testid="os-topbar"
      className="absolute top-0 left-0 right-0 h-7 z-[410] flex items-center justify-between px-3 text-[11px] cmdstrip mono"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5" data-testid="topbar-brand">
          <img
            src="/hexa-brand.png"
            alt="HEXA"
            className="h-4 w-auto max-w-[110px] object-contain"
            style={{ filter: "drop-shadow(0 0 6px rgba(139,92,246,0.55))" }}
          />
          <BetaChip size="xs" tone="outline" />
        </div>
        <button
          data-testid="topbar-buddy"
          onClick={() => { dismissBuddyTip(); setBuddyOpen(true); }}
          title="Ask HEXA Buddy — your AI guide"
          className="hidden sm:flex items-center gap-1 uppercase tracking-[0.22em] text-[10px] font-semibold transition relative"
          style={{
            padding: "2px 8px",
            color: "#fff",
            background: "linear-gradient(120deg, rgba(139,92,246,0.32), rgba(56,189,248,0.22))",
            border: "1px solid rgba(139,92,246,0.6)",
            clipPath: "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
          }}
        >
          <Sparkles size={11} className="text-[#c7c1ff]" />
          <span>buddy</span>
          {buddyTipVisible && (
            <span
              className="absolute rounded-full"
              style={{
                top: -2, right: -2, width: 7, height: 7,
                background: "#fbbf24",
                boxShadow: "0 0 0 0 rgba(251,191,36,0.65)",
                animation: "buddy-pulse-dot 1.6s ease-out infinite",
              }}
            />
          )}
        </button>
        {buddyTipVisible && (
          <div
            data-testid="buddy-tip-bubble"
            role="status"
            className="hidden md:flex items-center gap-1.5 mono relative"
            onClick={dismissBuddyTip}
            style={{
              padding: "4px 10px 4px 24px",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#0b0b1e",
              background: "linear-gradient(120deg, #fbbf24, #f472b6, #a78bfa)",
              border: "1px solid rgba(255,255,255,0.6)",
              borderRadius: 999,
              cursor: "pointer",
              animation: "buddy-tip-slide 380ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
            title="Click to dismiss"
          >
            <Sparkles
              size={10}
              className="absolute left-2 top-1/2 -translate-y-1/2"
              style={{ color: "#0b0b1e", animation: "buddy-tip-sparkle 1.4s ease-in-out infinite" }}
            />
            <span>Buddy is thinking of a tip for you</span>
            <span style={{ display: "inline-flex", gap: 2, marginLeft: 2 }} aria-hidden="true">
              <span className="buddy-dot" style={{ animationDelay: "0ms" }}>·</span>
              <span className="buddy-dot" style={{ animationDelay: "160ms" }}>·</span>
              <span className="buddy-dot" style={{ animationDelay: "320ms" }}>·</span>
            </span>
          </div>
        )}
        {!appAccess?.hidden?.includes("crm") && (
        <button
          data-testid="topbar-crm"
          onClick={() => { const app = APPS.find(a => a.id === "crm"); if (app) openApp(app); }}
          title="CRM"
          className="hidden sm:flex items-center gap-1 uppercase tracking-[0.22em] text-[10px] font-semibold transition"
          style={{
            padding: "2px 8px",
            color: "#fff",
            background: "linear-gradient(120deg, rgba(236,72,153,0.28), rgba(244,114,182,0.18))",
            border: "1px solid rgba(236,72,153,0.5)",
            clipPath: "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
          }}
        >
          <Users size={11} className="text-[#f9a8d4]" />
          <span>crm</span>
        </button>
        )}
        {!appAccess?.hidden?.includes("projects") && (
        <button
          data-testid="topbar-project"
          onClick={() => { const app = APPS.find(a => a.id === "projects"); if (app) openApp(app); }}
          title="Projects — unified workspace with tasks, phases and finance"
          className="hidden sm:flex items-center gap-1 uppercase tracking-[0.22em] text-[10px] font-semibold transition"
          style={{
            padding: "2px 8px",
            color: "#fff",
            background: "linear-gradient(120deg, rgba(249,115,22,0.28), rgba(251,146,60,0.18))",
            border: "1px solid rgba(249,115,22,0.5)",
            clipPath: "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
          }}
        >
          <FolderKanban size={11} className="text-[#fdba74]" />
          <span>project</span>
        </button>
        )}
        {hasOpenApps && (
          <button
            data-testid="topbar-taskbar-toggle"
            onClick={() => {
              // Desktop never passed onToggleTaskbar, so this button was inert.
              // Taskbar owns its own visibility and listens for this event.
              if (typeof onToggleTaskbar === "function") onToggleTaskbar();
              window.dispatchEvent(new CustomEvent("hexa:toggle-taskbar"));
              setTaskbarShown((v) => !v);
            }}
            title={(taskbarOpen ?? taskbarShown) ? "Hide taskbar" : "Show taskbar"}
            className="flex items-center gap-1 uppercase tracking-[0.22em] text-[10px] font-semibold transition"
            style={{
              padding: "2px 8px",
              color: "#fff",
              background: (taskbarOpen ?? taskbarShown)
                ? "linear-gradient(120deg, rgba(34,211,238,0.55), rgba(139,92,246,0.55))"
                : "linear-gradient(120deg, rgba(34,211,238,0.22), rgba(139,92,246,0.18))",
              border: `1px solid ${taskbarOpen ? "rgba(34,211,238,0.85)" : "rgba(34,211,238,0.5)"}`,
              clipPath: "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
            }}
          >
            <LayoutGrid size={11} className="text-[#67e8f9]" />
            <span>taskbar</span>
            {taskbarOpen && (
              <span className="ml-1 w-1.5 h-1.5 rounded-full" style={{ background: "#34d399", boxShadow: "0 0 6px #34d399" }} />
            )}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-3 text-[#c7c1ff]/80">
        {voiceFeedback && (
          <span className="text-[#3b82f6] text-[10px] uppercase tracking-wider">{voiceFeedback}</span>
        )}
        {!appAccess?.hidden?.includes("wingman") && (
        <button
          data-testid="topbar-novo"
          onClick={() => {
            const app = APPS.find(a => a.id === "wingman");
            if (app) openApp(app);
          }}
          className="relative flex items-center gap-1 uppercase tracking-[0.22em] text-[10px] font-semibold transition"
          style={{
            padding: "2px 8px",
            color: "#fff",
            background: "linear-gradient(120deg, rgba(139,92,246,0.35), rgba(56,189,248,0.28))",
            border: "1px solid rgba(167,139,250,0.55)",
            clipPath: "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
          }}
          title="Novo — HEXA's agentic AI copilot"
        >
          <Sparkles size={11} className="text-[#a78bfa]" />
          <span>novo</span>
          <span
            className="ml-0.5 w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: "#34d399", boxShadow: "0 0 6px #34d399" }}
          />
        </button>
        )}
        {!appAccess?.hidden?.includes("approvals") && (
        <button
          data-testid="topbar-approvals"
          onClick={() => {
            const app = APPS.find(a => a.id === "approvals");
            if (app) openApp(app);
          }}
          className={`relative flex items-center gap-1 uppercase tracking-wider text-[10px] ${pendingCount > 0 ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
          title="AI draft approvals queue"
        >
          <CheckSquare size={12} />
          <span className="hidden sm:inline">approvals</span>
          {pendingCount > 0 && (
            <span
              className="ml-0.5 px-1.5 py-0.5 text-[9px] font-bold mono"
              style={{ background: "#3b82f6", color: "#0b1014", lineHeight: 1, minWidth: 18, textAlign: "center" }}
            >{pendingCount}</span>
          )}
        </button>
        )}
        {!appAccess?.hidden?.includes("teamchat") && (
        <button
          data-testid="topbar-teamchat"
          onClick={() => {
            const app = APPS.find(a => a.id === "teamchat");
            if (app) openApp(app);
          }}
          className={`relative flex items-center gap-1 uppercase tracking-wider text-[10px] ${chatUnread > 0 ? "text-[#8b5cf6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
          title="Team chat (DMs · Spaces)"
        >
          <Hash size={12} />
          <span className="hidden sm:inline">chat</span>
          {chatUnread > 0 && (
            <span
              data-testid="topbar-chat-unread"
              className="ml-0.5 px-1.5 py-0.5 text-[9px] font-bold mono"
              style={{ background: "#8b5cf6", color: "#0b1014", lineHeight: 1, minWidth: 18, textAlign: "center" }}
            >{chatUnread}</span>
          )}
        </button>
        )}
        <button
          data-testid="topbar-voice"
          onClick={toggleVoice}
          className={`flex items-center gap-1 ${voiceOn ? "text-[#3b82f6] pulse-amber rounded-full px-1.5" : "hover:text-[#8b5cf6]"}`}
          title='Voice command — say "open chat", "new task", etc.'
        >
          {voiceOn ? <MicOff size={13} /> : <Mic size={13} />}
        </button>
        <button
          data-testid="topbar-gesture-help"
          onClick={() => window.dispatchEvent(new CustomEvent("hexa-gesture-help"))}
          className="hidden sm:flex items-center gap-1 text-[#c7c1ff]/55 hover:text-[#8b5cf6] uppercase tracking-wider text-[10px]"
          title="Voice & gesture cheat sheet (⌘+,)"
        >
          <span className="mono">⌘,</span>
        </button>
        {installEvt && !installed && (
          <button
            data-testid="topbar-install"
            onClick={install}
            className="hidden sm:flex items-center gap-1 text-[#3b82f6] hover:brightness-110 uppercase tracking-wider text-[10px]"
            title="Install HEXA OS as an app"
          >
            <Download size={12} strokeWidth={1.5} />
            <span>install</span>
          </button>
        )}
        {!installEvt && !installed && (
          <button
            data-testid="topbar-install-help"
            onClick={install}
            className="hidden sm:flex items-center gap-1 text-[#c7c1ff]/65 hover:text-[#3b82f6] uppercase tracking-wider text-[10px]"
            title="How to install HEXA OS"
          >
            <Download size={12} strokeWidth={1.5} />
            <span>install</span>
          </button>
        )}
        <button
          data-testid="topbar-spotlight"
          onClick={onOpenSpotlight}
          className="hidden md:flex items-center gap-1.5 hover:text-[#8b5cf6] uppercase tracking-wider text-[10px]"
        >
          <Search size={12} strokeWidth={1.5} />
          <span className="text-[#c7c1ff]/60">cmd · ⌘K</span>
        </button>
        <span className="hidden md:inline text-[#c7c1ff]/30">|</span>
        <Volume2 size={12} strokeWidth={1.5} className="hidden md:inline" />
        {/* Online / Offline / Pending Sync pill */}
        <button
          data-testid="topbar-netstate"
          onClick={() => netState.online && offlineQueue.flush()}
          title={
            !netState.online
              ? `Offline — ${netState.pending} change${netState.pending === 1 ? "" : "s"} pending. Will auto-sync when back online.`
              : netState.pending > 0
                ? `${netState.pending} change${netState.pending === 1 ? "" : "s"} pending — click to sync now`
                : "Online · all changes synced"
          }
          className={`flex items-center gap-1 uppercase tracking-wider text-[10px] ${
            !netState.online
              ? "text-[#f87171]"
              : netState.pending > 0
                ? "text-[#3b82f6]"
                : "text-[#c7c1ff]/60"
          }`}
        >
          {!netState.online ? (
            <><WifiOff size={11} strokeWidth={1.5} /><span>offline</span></>
          ) : netState.syncing ? (
            <><RefreshCw size={11} strokeWidth={1.5} className="animate-spin" /><span>sync</span></>
          ) : netState.pending > 0 ? (
            <><CloudOff size={11} strokeWidth={1.5} /><span>{netState.pending} pending</span></>
          ) : (
            <><Wifi size={11} strokeWidth={1.5} /><span className="hidden sm:inline">online</span></>
          )}
        </button>
        <BatteryFull size={13} strokeWidth={1.5} className="hidden sm:inline" />
        <span className="hidden sm:inline text-[#c7c1ff]/30">|</span>
        <span className="hidden md:inline tracking-wider text-[10px] uppercase text-[#c7c1ff]/70">{date}</span>
        <span className="font-semibold tracking-wider text-[#3b82f6]">{time}</span>
        {user && sessionElapsed && (
          <span
            data-testid="topbar-session-timer"
            className="hidden lg:flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full"
            style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.25)", color: "#86efac" }}
            title={`Session started: ${loginTime.toLocaleTimeString()}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
            {sessionElapsed}
          </span>
        )}
        {user && (
          <button
            data-testid="topbar-sticky-notes-toggle"
            onClick={onToggleStickyNotes}
            title="Sticky Notes"
            className={`flex items-center gap-1 uppercase tracking-wider text-[10px] transition-colors ${stickyNotesVisible ? "text-[#fbbf24]" : "text-[#c7c1ff]/60 hover:text-[#fbbf24]"}`}
          >
            <StickyNote size={13} />
          </button>
        )}
        {user && (
          <div ref={menuRef} className="relative">
            <button
              data-testid="topbar-user"
              onClick={() => setMenuOpen(o => !o)}
              className="flex items-center gap-1.5 hover:text-[#8b5cf6]"
            >
              <span
                className="w-5 h-5 text-[#3b82f6] text-[10px] flex items-center justify-center font-bold overflow-hidden"
                style={{
                  clipPath: user?.photo ? undefined : "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                  borderRadius: user?.photo ? "50%" : undefined,
                  background: user?.photo ? undefined : "rgba(59,130,246,0.16)",
                  border: user?.photo ? "1px solid rgba(59,130,246,0.4)" : "1px solid rgba(59,130,246,0.4)",
                }}
              >{user?.photo ? <img src={user.photo} alt="" className="w-full h-full object-cover" /> : initial}</span>
              <span className="hidden sm:inline font-medium tracking-wider uppercase text-[10px]">{user.name || user.username}</span>
            </button>
            {menuOpen && (
              // Elevated deliberately. Without a shadow this menu was a flat slab
              // butting against the app underneath, which read as a broken layout
              // rather than a dropdown floating above it. It already dismisses on
              // click-away, so nothing here is mispositioned — it just needed to
              // look like a menu.
              <div
                className="absolute right-0 top-7 w-60 panel-strong p-1 z-[200] rounded-lg overflow-hidden"
                style={{
                  boxShadow: "0 18px 44px rgba(0,0,0,0.55), 0 0 0 1px rgba(139,92,246,0.28)",
                }}
              >
                <div className="px-3 py-2 border-b border-[#8b5cf6]/15">
                  <div className="flex items-center gap-2">
                    {user.photo ? (
                      <img src={user.photo} alt="" className="w-8 h-8 rounded-full object-cover border border-[#a78bfa]/40" />
                    ) : (
                      <div className="w-8 h-8 rounded-full grid place-items-center text-xs font-semibold text-white flex-shrink-0"
                        style={{ background: "linear-gradient(135deg, #a78bfa, #3b82f6)" }}>
                        {initial}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">{user.name}</div>
                      <div className="text-[10px] text-[#c7c1ff]/60 mono truncate">{user.address}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-[#3b82f6] mt-1 flex items-center gap-1 uppercase tracking-widest"><User size={9} /> HEXA ID</div>
                </div>
                <button
                  data-testid="topbar-my-profile"
                  onClick={() => { setMenuOpen(false); setProfileOpen(true); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#8b5cf6]/5 flex items-center gap-2 text-[#c7c1ff] uppercase tracking-widest"
                >
                  <UserCog size={12} /> my profile
                </button>
                <button
                  data-testid="topbar-logout"
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#8b5cf6]/5 flex items-center gap-2 text-[#f87171] uppercase tracking-widest"
                >
                  <LogOut size={12} /> sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <Profile
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onOpenApp={(id) => { const app = APPS.find(a => a.id === id); if (app) openApp(app); }}
      />
      {installHelpOpen && createPortal(
        <div
          data-testid="install-help-modal"
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[400]"
          onClick={() => setInstallHelpOpen(false)}
        >
          <div
            className="w-[440px] p-4"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "rgba(14,26,34,0.98)",
              border: "1px solid rgba(139,92,246,0.25)",
              clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="mono text-[11px] uppercase tracking-widest text-[#8b5cf6]">// install hexa os</div>
              <button onClick={() => setInstallHelpOpen(false)} className="text-white/60 hover:text-white text-sm">×</button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <span
                className="inline-block w-10 h-12"
                style={{
                  clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                  background: "linear-gradient(160deg, #8b5cf6, #0891b2)",
                }}
              />
              <div>
                <div className="text-sm font-medium">HEXA OS · standalone app</div>
                <div className="text-[10px] mono text-[#c7c1ff]/60 uppercase tracking-widest">offline shell · push · native window</div>
              </div>
            </div>
            <div className="text-xs text-white/85 mb-3 leading-relaxed">{browserHint}</div>
            <ul className="text-[11px] text-white/70 space-y-1 mb-3 list-disc pl-4">
              <li>Works offline (cached app shell)</li>
              <li>Receives push notifications even when closed</li>
              <li>Launches in its own window — no browser chrome</li>
            </ul>
            <div className="flex justify-end">
              <button
                data-testid="install-help-close"
                onClick={() => setInstallHelpOpen(false)}
                className="btn-accent text-xs"
              >Got it</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* HEXA Buddy — externally-controlled from the top-bar 'buddy' button. */}
      <HexaBuddy open={buddyOpen} onClose={() => setBuddyOpen(false)} hideLauncher />
    </div>
  );
}
